import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, Download, Copy } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { field } from "@/pages/Auth";

const btn = "gold-btn inline-flex items-center justify-center w-full h-14 rounded-full text-lg disabled:opacity-60";
const link = "text-[#C9B6FF] underline underline-offset-4 hover:text-[#EDE6F7]";

function ErrorLine({ error }) {
  return error ? <p role="alert" className="rounded-xl border border-[#6B2A4A] bg-[#1E1428] px-4 py-3 text-[#E6CCF2]" data-testid="mfa-error">{error}</p> : null;
}

function CodeField({ value, onChange, recovery }) {
  return (
    <div>
      <label htmlFor="mfa-code" className="block text-[#EDE6F7] font-semibold mb-2">{recovery ? "Recovery code" : "6-digit code"}</label>
      <input id="mfa-code" autoFocus autoComplete="one-time-code" inputMode={recovery ? "text" : "numeric"} maxLength={recovery ? 14 : 7}
        placeholder={recovery ? "XXXXX-XXXXX" : "123456"} className={`${field} tracking-[0.3em] text-center text-2xl`}
        value={value} onChange={(e) => onChange(e.target.value)} data-testid="mfa-code-input" />
    </div>
  );
}

export function RecoveryCodes({ codes, onDone, doneLabel = "Continue" }) {
  const [saved, setSaved] = useState(false);
  const text = `Memories Forever recovery codes\nEach code works once.\n\n${codes.join("\n")}\n`;
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    a.download = "memories-forever-recovery-codes.txt";
    a.click();
  };
  return (
    <div className="space-y-5" data-testid="mfa-recovery-codes">
      <h2 className="font-serif text-2xl text-[#EDE6F7]">Save your recovery codes</h2>
      <p className="text-[#B9B3C8]">If you lose your phone, each code lets you log in once. Keep them somewhere safe, such as a password manager. They won't be shown again.</p>
      <ul className="grid grid-cols-2 gap-2 rounded-2xl border border-[#33264A] bg-[#150F1C] p-4 font-mono text-lg text-[#EDE6F7]">
        {codes.map((c) => <li key={c} className="text-center">{c}</li>)}
      </ul>
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={download} className="inline-flex items-center gap-2 rounded-full border border-[#8B2CFF] px-4 py-2 text-[#EDE6F7] hover:bg-[#241634]" data-testid="mfa-codes-download"><Download className="h-4 w-4" />Download</button>
        <button type="button" onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Codes copied"))} className="inline-flex items-center gap-2 rounded-full border border-[#8B2CFF] px-4 py-2 text-[#EDE6F7] hover:bg-[#241634]" data-testid="mfa-codes-copy"><Copy className="h-4 w-4" />Copy</button>
      </div>
      <label className="flex items-center gap-3 text-[#EDE6F7]"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} className="h-5 w-5 accent-[#8B2CFF]" data-testid="mfa-codes-saved" />I've saved my recovery codes</label>
      <button type="button" disabled={!saved} onClick={onDone} className={btn} data-testid="mfa-codes-continue">{doneLabel}</button>
    </div>
  );
}

function Setup({ challenge, onDone }) {
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.post("/auth/mfa/setup/start", { challenge }).then((r) => setQr(r.data)).catch((e) => setError(apiError(e))); }, [challenge]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { onDone((await api.post("/auth/mfa/setup/confirm", { challenge, code })).data); }
    catch (err) { setError(apiError(err)); setBusy(false); }
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-5" data-testid="mfa-setup">
      <h1 className="font-serif text-3xl text-[#EDE6F7]">Set up two-step verification</h1>
      <p className="text-[#B9B3C8]">Administrator accounts need a code from an authenticator app (such as Google Authenticator, Microsoft Authenticator or 1Password) each time they log in.</p>
      <ol className="list-decimal pl-5 space-y-2 text-[#C9C2D8]"><li>Open your authenticator app and add a new account.</li><li>Scan this QR code, or type the key below.</li><li>Enter the 6-digit code the app shows.</li></ol>
      {qr && <div className="flex flex-col items-center gap-3">
        <img src={qr.qr_svg_data} alt="QR code for your authenticator app" className="h-52 w-52 rounded-xl bg-white p-2" data-testid="mfa-qr" />
        <p className="text-sm text-[#A49CB8]">Can't scan? Enter this key:</p>
        <code className="rounded-lg bg-[#150F1C] px-3 py-2 text-[#EDE6F7] break-all text-center" data-testid="mfa-manual-key">{qr.manual_key}</code>
      </div>}
      <CodeField value={code} onChange={setCode} />
      <ErrorLine error={error} />
      <button type="submit" disabled={busy || !qr} className={btn} data-testid="mfa-setup-submit">{busy ? "Checking…" : "Turn on two-step verification"}</button>
    </form>
  );
}

function Verify({ challenge, onDone }) {
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { onDone((await api.post("/auth/mfa/verify", { challenge, code, recovery })).data); }
    catch (err) { setError(apiError(err)); setBusy(false); }
  };
  const lost = async () => {
    if (!window.confirm("Reset two-step verification? We'll email you a link. For your security, the reset can only be completed after 24 hours.")) return;
    try { setSent((await api.post("/auth/mfa/recovery/request", { challenge })).data.message); } catch (err) { setError(apiError(err)); }
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-5" data-testid="mfa-verify">
      <h1 className="font-serif text-3xl text-[#EDE6F7]">Enter your verification code</h1>
      <p className="text-[#B9B3C8]">{recovery ? "Enter one of your saved recovery codes. Each code works once." : "Open your authenticator app and enter the 6-digit code for Memories Forever."}</p>
      <CodeField value={code} onChange={setCode} recovery={recovery} />
      <ErrorLine error={error} />
      {sent && <p className="rounded-xl border border-[#4A3570] bg-[#1A1222] px-4 py-3 text-[#C9B6FF]" data-testid="mfa-recovery-sent">{sent}</p>}
      <button type="submit" disabled={busy} className={btn} data-testid="mfa-verify-submit">{busy ? "Checking…" : "Log in"}</button>
      <div className="flex flex-col items-center gap-3 text-sm">
        <button type="button" className={link} onClick={() => { setRecovery(!recovery); setCode(""); setError(""); }} data-testid="mfa-toggle-recovery">{recovery ? "Use my authenticator app instead" : "Use a recovery code"}</button>
        <button type="button" className={link} onClick={lost} data-testid="mfa-lost-device">Lost your phone and recovery codes?</button>
      </div>
    </form>
  );
}

export default function TwoStep() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { login, refresh } = useAuth();
  const [codes, setCodes] = useState(null);
  const [pending, setPending] = useState(null);
  if (!state?.challenge) return <Navigate to="/signin?mode=login" replace />;
  const finish = async (d) => { login(d.token, d.user); await refresh(); navigate("/admin", { replace: true }); };
  const onDone = (d) => (d.recovery_codes ? (setPending(d), setCodes(d.recovery_codes)) : finish(d));
  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-lg mx-auto px-4 py-10">
        <div className="glass-card p-7 sm:p-9 space-y-4" data-testid="two-step-page">
          <ShieldCheck className="h-8 w-8 text-[#B57BFF]" aria-hidden="true" />
          {codes ? <RecoveryCodes codes={codes} onDone={() => finish(pending)} />
            : state.mode === "setup" ? <Setup challenge={state.challenge} onDone={onDone} /> : <Verify challenge={state.challenge} onDone={onDone} />}
        </div>
      </main>
    </div>
  );
}
